import { useState, useEffect } from "react";
import { Download, FileText, FileCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import {
  downloadTest,
  testToTestLike,
  type DownloadFormat,
  type TestSection,
} from "@/utils/downloadTest";
import type { Test } from "@shared/schema";

// Re-download dialog used from the Dashboard's per-row "Download…" action.
// Mirrors the Share page's download controls (section checkboxes + format
// radio + single-file toggle), but skips ShareTest's other concerns (save,
// stats, navigation). Receives a full `Test` so it doesn't need TestContext.

interface DownloadTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  test: Test | null;
}

export function DownloadTestDialog({ open, onOpenChange, test }: DownloadTestDialogProps) {
  const { toast } = useToast();
  const [selectedItems, setSelectedItems] = useState({
    blueprint: false,
    studentCopy: false,
    answerKey: false,
    teacherCopy: false,
  });
  const [fileType, setFileType] = useState<DownloadFormat>("pdf");
  const [downloadAsSingleFile, setDownloadAsSingleFile] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [filename, setFilename] = useState("");

  // Seed the file-name field from the test name when the dialog opens, and
  // clear the section / format / single-file selections when it closes so it
  // always opens fresh (and never carries one test's picks over to another).
  useEffect(() => {
    if (open && test) {
      setFilename(test.name);
    } else if (!open) {
      setSelectedItems({
        blueprint: false,
        studentCopy: false,
        answerKey: false,
        teacherCopy: false,
      });
      setFileType("pdf");
      setDownloadAsSingleFile(false);
    }
  }, [open, test]);

  const handleItemSelection = (item: keyof typeof selectedItems, checked: boolean) => {
    setSelectedItems((prev) => ({ ...prev, [item]: checked }));
  };

  const handleDownload = async () => {
    if (!test) return;
    const ordered: TestSection[] = [];
    if (selectedItems.blueprint) ordered.push("blueprint");
    if (selectedItems.studentCopy) ordered.push("studentCopy");
    if (selectedItems.answerKey) ordered.push("answerKey");
    if (selectedItems.teacherCopy) ordered.push("teacherCopy");

    if (ordered.length === 0) {
      toast({
        title: "No items selected",
        description: "Pick at least one section to download.",
        variant: "destructive",
      });
      return;
    }

    setIsDownloading(true);
    try {
      await downloadTest(testToTestLike(test), {
        format: fileType,
        sections: ordered,
        singleFile: downloadAsSingleFile,
        filenameBase: filename.trim() || test.name,
      });
      const labelByFormat: Record<DownloadFormat, string> = {
        pdf: "PDF",
        docx: "DOCX",
        txt: "TXT",
        md: "Markdown",
      };
      toast({
        title: "Download complete",
        description: downloadAsSingleFile
          ? `Combined ${labelByFormat[fileType]} downloaded`
          : `${ordered.length} ${labelByFormat[fileType]} file${ordered.length === 1 ? "" : "s"} downloaded`,
      });
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to re-download test:", err);
      toast({
        title: "Download failed",
        description: "Could not build the file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Download {test?.name ?? "test"}</DialogTitle>
          <DialogDescription>
            Pick which sections to include and the file format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* File name */}
          <div className="space-y-2">
            <Label htmlFor="dl-filename" className="text-sm font-medium">
              File name
            </Label>
            <Input
              id="dl-filename"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder={test?.name ?? "Test"}
              data-testid="input-download-filename"
            />
            <p className="text-xs text-muted-foreground">
              Each section file uses this name plus its label (e.g. _Student_Copy). Tick
              &ldquo;Download as single file&rdquo; to get one combined file named exactly this.
            </p>
          </div>

          {/* Item Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Sections</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div
                className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => handleItemSelection("blueprint", !selectedItems.blueprint)}
              >
                <Checkbox
                  id="dl-blueprint"
                  checked={selectedItems.blueprint}
                  onCheckedChange={(c) => handleItemSelection("blueprint", c as boolean)}
                />
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <Label htmlFor="dl-blueprint" className="text-sm font-medium cursor-pointer">
                      Blueprint
                    </Label>
                    <p className="text-xs text-muted-foreground">Topic breakdown &amp; stats</p>
                  </div>
                </div>
              </div>

              <div
                className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => handleItemSelection("studentCopy", !selectedItems.studentCopy)}
              >
                <Checkbox
                  id="dl-student"
                  checked={selectedItems.studentCopy}
                  onCheckedChange={(c) => handleItemSelection("studentCopy", c as boolean)}
                />
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <Label htmlFor="dl-student" className="text-sm font-medium cursor-pointer">
                      Student Copy
                    </Label>
                    <p className="text-xs text-muted-foreground">Questions only</p>
                  </div>
                </div>
              </div>

              <div
                className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => handleItemSelection("answerKey", !selectedItems.answerKey)}
              >
                <Checkbox
                  id="dl-key"
                  checked={selectedItems.answerKey}
                  onCheckedChange={(c) => handleItemSelection("answerKey", c as boolean)}
                />
                <div className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <Label htmlFor="dl-key" className="text-sm font-medium cursor-pointer">
                      Answer Key
                    </Label>
                    <p className="text-xs text-muted-foreground">With correct answers</p>
                  </div>
                </div>
              </div>

              <div
                className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => handleItemSelection("teacherCopy", !selectedItems.teacherCopy)}
              >
                <Checkbox
                  id="dl-teacher"
                  checked={selectedItems.teacherCopy}
                  onCheckedChange={(c) => handleItemSelection("teacherCopy", c as boolean)}
                />
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <Label htmlFor="dl-teacher" className="text-sm font-medium cursor-pointer">
                      Teacher Copy
                    </Label>
                    <p className="text-xs text-muted-foreground">Questions with notes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* File Type */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">File type</Label>
            <RadioGroup
              value={fileType}
              onValueChange={(v) => setFileType(v as DownloadFormat)}
              className="flex flex-wrap gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pdf" id="dl-fmt-pdf" />
                <Label htmlFor="dl-fmt-pdf" className="text-sm cursor-pointer">PDF</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="docx" id="dl-fmt-docx" />
                <Label htmlFor="dl-fmt-docx" className="text-sm cursor-pointer">DOCX</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="txt" id="dl-fmt-txt" />
                <Label htmlFor="dl-fmt-txt" className="text-sm cursor-pointer">TXT</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="md" id="dl-fmt-md" />
                <Label htmlFor="dl-fmt-md" className="text-sm cursor-pointer">Markdown</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Single file toggle */}
          <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50">
            <Checkbox
              id="dl-single"
              checked={downloadAsSingleFile}
              onCheckedChange={(c) => setDownloadAsSingleFile(c as boolean)}
            />
            <div>
              <Label htmlFor="dl-single" className="text-sm font-medium cursor-pointer">
                Download as single file
              </Label>
              <p className="text-xs text-muted-foreground">
                Combine all selected sections into one file
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDownloading}>
            Cancel
          </Button>
          <Button onClick={handleDownload} disabled={isDownloading || !test}>
            {isDownloading ? "Preparing…" : "Download"} <Download className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
